import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { getJSON, postJSON } from '@/lib/api'

export interface AudienceOption {
  id: number
  name: string
}

export interface AudienceEstimate {
  recipients: number
  excluded: number
  no_consent_basis: number
  exclusions?: AudienceExclusion[]
}

interface AudienceExclusion {
  contact_id: number
  contact_name: string
  reason: string
}

export function consentEvidenceWarning(count: number) {
  const recipient = count === 1 ? 'recipient' : 'recipients'
  return `${count} ${recipient} without consent evidence`
}

export function BlastAudienceRail({ audience }: { audience: ReturnType<typeof useBlastAudience> }) {
  const [pickerOpen, setPickerOpen] = React.useState(false)
  return (
    <Card>
      <CardContent className="divide-y divide-border p-0">
        <section className="space-y-2 px-4 py-3">
          <p className="text-caption text-muted-foreground">To</p>
          <div className="flex flex-wrap gap-1.5">
            {selectedAudienceBadges(audience.selectedSegments, audience.segments, 'Segment')}
            {selectedAudienceBadges(audience.selectedGroups, audience.groups, 'Group')}
            {selectedAudienceBadges(audience.selectedContacts, audience.contacts, 'Contact')}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-auto border-dashed px-1.5 py-0.5 text-micro"
              onClick={() => setPickerOpen((current) => !current)}
            >
              + Add
            </Button>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-normal tracking-tight text-foreground">
              ~{audience.estimate.recipients}
            </span>
            <span className="text-body-sm text-muted-foreground">
              recipients · resolved at send
            </span>
          </div>
          <p className="text-caption text-muted-foreground">
            <span className="text-warning">{audience.estimate.excluded}</span> excluded
          </p>
          <AudienceExclusions exclusions={audience.estimate.exclusions} />
        </section>
        {audience.estimate.no_consent_basis > 0 ? (
          <section className="bg-warning/10 px-4 py-3 text-body-sm text-foreground">
            {consentEvidenceWarning(audience.estimate.no_consent_basis)}
          </section>
        ) : null}
        {pickerOpen ? <AudiencePicker audience={audience} /> : null}
      </CardContent>
    </Card>
  )
}

export function AudienceExclusions({ exclusions = [] }: { exclusions?: AudienceExclusion[] }) {
  if (exclusions.length === 0) return null

  return (
    <ul className="space-y-1 text-caption text-muted-foreground">
      {exclusions.map((exclusion) => (
        <li key={exclusion.contact_id}>
          {exclusion.contact_name} — {exclusion.reason}
        </li>
      ))}
    </ul>
  )
}

function AudiencePicker({ audience }: { audience: ReturnType<typeof useBlastAudience> }) {
  return (
    <section
      data-testid="blast-audience-picker"
      className="max-h-48 space-y-3 overflow-y-auto px-4 py-3"
    >
      <AudienceChoices
        label="Segments"
        options={audience.segments}
        selected={audience.selectedSegments}
        setSelected={audience.setSelectedSegments}
      />
      <AudienceChoices
        label="Groups"
        options={audience.groups}
        selected={audience.selectedGroups}
        setSelected={audience.setSelectedGroups}
      />
      <AudienceChoices
        label="Contacts"
        options={audience.contacts}
        selected={audience.selectedContacts}
        setSelected={audience.setSelectedContacts}
      />
    </section>
  )
}

function AudienceChoices({
  label,
  options,
  selected,
  setSelected,
}: {
  label: string
  options: AudienceOption[]
  selected: Set<number>
  setSelected: React.Dispatch<React.SetStateAction<Set<number>>>
}) {
  return (
    <fieldset className="space-y-1">
      <legend className="text-caption font-medium text-foreground">{label}</legend>
      {options.map((option) => (
        <label key={option.id} className="flex items-center gap-2 py-1 text-body-sm">
          <Checkbox
            checked={selected.has(option.id)}
            onCheckedChange={(checked) =>
              setSelection(setSelected, selected, option.id, checked === true)
            }
          />
          <span>{option.name}</span>
        </label>
      ))}
    </fieldset>
  )
}

function selectedAudienceBadges(selected: Set<number>, options: AudienceOption[], kind: string) {
  return options
    .filter((option) => selected.has(option.id))
    .map((option) => (
      <Badge key={`${kind}-${option.id}`} variant={kind === 'Segment' ? 'person' : 'neutral'}>
        {kind} · {option.name}
      </Badge>
    ))
}

export function useBlastAudience(open: boolean) {
  const [selectedContacts, setSelectedContacts] = React.useState<Set<number>>(new Set())
  const [selectedSegments, setSelectedSegments] = React.useState<Set<number>>(new Set())
  const [selectedGroups, setSelectedGroups] = React.useState<Set<number>>(new Set())
  const contactsQuery = useAudienceOptions('contacts', open)
  const segmentsQuery = useAudienceOptions('segments', open)
  const groupsQuery = useAudienceOptions('groups', open)
  const payload = React.useMemo(
    () => ({
      segment_ids: Array.from(selectedSegments),
      group_ids: Array.from(selectedGroups),
      contact_ids: Array.from(selectedContacts),
    }),
    [selectedContacts, selectedGroups, selectedSegments],
  )
  const selectedCount = selectedContacts.size + selectedSegments.size + selectedGroups.size
  const estimateQuery = useQuery({
    queryKey: ['blast-estimate', payload],
    queryFn: () =>
      postJSON<{ estimate: AudienceEstimate }>('/api/v1/blasts/estimate', { blast: payload }),
    enabled: open && selectedCount > 0,
    retry: false,
  })
  const reset = () => {
    setSelectedContacts(new Set())
    setSelectedSegments(new Set())
    setSelectedGroups(new Set())
  }
  const load = (selection: {
    segments: AudienceOption[]
    groups: AudienceOption[]
    contacts: AudienceOption[]
  }) => {
    setSelectedSegments(new Set(selection.segments.map((option) => option.id)))
    setSelectedGroups(new Set(selection.groups.map((option) => option.id)))
    setSelectedContacts(new Set(selection.contacts.map((option) => option.id)))
  }
  return {
    payload,
    selectedContacts,
    selectedSegments,
    selectedGroups,
    setSelectedContacts,
    setSelectedSegments,
    setSelectedGroups,
    contacts: contactsQuery.data ?? [],
    segments: segmentsQuery.data ?? [],
    groups: groupsQuery.data ?? [],
    estimate: estimateQuery.data?.estimate ?? {
      recipients: 0,
      excluded: 0,
      no_consent_basis: 0,
    },
    reset,
    load,
  }
}

function useAudienceOptions(kind: 'contacts' | 'segments' | 'groups', enabled: boolean) {
  return useQuery({
    queryKey: [kind, 'blast-composer'],
    queryFn: async () => {
      const response = await getJSON<Record<string, AudienceOption[]>>(`/api/v1/${kind}`)
      return response[kind]
    },
    enabled,
    retry: false,
  })
}

function setSelection(
  update: React.Dispatch<React.SetStateAction<Set<number>>>,
  current: Set<number>,
  id: number,
  checked: boolean,
) {
  const next = new Set(current)
  if (checked) next.add(id)
  else next.delete(id)
  update(next)
}
